import { ActivityIcon, ClockIcon, MapPinIcon, VideoIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type { TimetableItem } from "../../domain/contracts/public-booking";
import { formatTimeDisplay, timeToMinutes } from "./timetable-utils";

const ALERT_MINUTES_BEFORE = 30;

function getTypeLabel(type: TimetableItem["type"]): string {
  switch (type) {
    case "class":
      return "ຄລາດສ໌";
    case "private":
      return "ວິຊາສ່ວນຕົວ";
    case "room_booking":
      return "ຈອງຫ້ອງ";
    default:
      return "";
  }
}

function getTypeIcon(type: TimetableItem["type"]) {
  switch (type) {
    case "class":
      return ActivityIcon;
    case "private":
      return VideoIcon;
    case "room_booking":
      return MapPinIcon;
    default:
      return ClockIcon;
  }
}

export type AlertBannerProps = {
  todayItems: TimetableItem[];
};

type AlertItem = {
  item: TimetableItem;
  isLive: boolean;
  countdownText: string;
};

export function AlertBanner({ todayItems }: AlertBannerProps) {
  const [now, setNow] = useState(new Date());

  // Update time every second for countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const currentSeconds = now.getSeconds();

  // Group items by roomId
  const itemsByRoom = new Map<string, TimetableItem[]>();
  for (const item of todayItems) {
    const roomItems = itemsByRoom.get(item.roomId) ?? [];
    roomItems.push(item);
    itemsByRoom.set(item.roomId, roomItems);
  }

  // Find alerts for each room
  const alerts: AlertItem[] = [];
  for (const [, roomItems] of itemsByRoom) {
    const sorted = [...roomItems].sort((a, b) =>
      a.startTime.localeCompare(b.startTime),
    );

    const currentSession = sorted.find((item) => {
      const start = timeToMinutes(item.startTime);
      const end = timeToMinutes(item.endTime);
      return currentMinutes >= start && currentMinutes < end;
    });

    const nextSession = sorted.find((item) => {
      const start = timeToMinutes(item.startTime);
      return start > currentMinutes;
    });

    const showNext =
      nextSession &&
      timeToMinutes(nextSession.startTime) - currentMinutes <=
        ALERT_MINUTES_BEFORE;

    const displayItem = currentSession ?? (showNext ? nextSession : null);

    if (!displayItem) continue;

    const isLive = !!currentSession;

    // Calculate countdown
    let countdownText = "";
    if (!isLive && nextSession) {
      const startMinutes = timeToMinutes(nextSession.startTime);
      const totalSecondsUntilStart =
        (startMinutes - currentMinutes) * 60 - currentSeconds;
      const minutesRemaining = Math.floor(totalSecondsUntilStart / 60);
      const secondsRemaining = totalSecondsUntilStart % 60;
      if (minutesRemaining >= 0 && secondsRemaining >= 0) {
        countdownText = `ເຫຼືອ ${minutesRemaining} ນາທີ${secondsRemaining > 0 ? ` ${secondsRemaining} ວິນາທີ` : ""}`;
      }
    }

    alerts.push({
      item: displayItem,
      isLive,
      countdownText,
    });
  }

  // Sort alerts: active first, then by countdown time
  alerts.sort((a, b) => {
    if (a.isLive && !b.isLive) return -1;
    if (!a.isLive && b.isLive) return 1;
    return a.item.startTime.localeCompare(b.item.startTime);
  });

  if (alerts.length === 0) {
    const hasAny = todayItems.length > 0;
    return (
      <div className="w-full rounded-lg border border-border bg-card px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3 text-muted-foreground">
          <ClockIcon className="size-5 shrink-0" />
          <span className="text-sm">
            {hasAny ? "ບໍ່ມີເຊດຊັນຖັດໄປ" : "ບໍ່ມີເຊດຊັນວັນນີ້"}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
      {alerts.map((alert, index) => {
        const startFormatted = formatTimeDisplay(alert.item.startTime);
        const endFormatted = formatTimeDisplay(alert.item.endTime);
        const TypeIcon = getTypeIcon(alert.item.type);

        return (
          <div
            key={`${alert.item.roomId}-${alert.item.id}-${index}`}
            className={`relative overflow-hidden rounded-lg border bg-card shadow-sm transition-all ${
              alert.isLive
                ? "border-l-4 border-l-primary bg-primary/5"
                : "border-l-4 border-l-amber-500 bg-amber-50/50 dark:bg-amber-950/10"
            }`}
          >
            <div className="flex items-start gap-4 px-4 py-3">
              {/* Status Indicator */}
              <div
                className={`mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-full ${
                  alert.isLive
                    ? "bg-primary/10 text-primary"
                    : "bg-amber-500/10 text-amber-600 dark:text-amber-500"
                }`}
              >
                {alert.isLive ? (
                  <ActivityIcon className="size-5 animate-pulse" />
                ) : (
                  <ClockIcon className="size-5" />
                )}
              </div>

              {/* Content */}
              <div className="flex flex-1 flex-col">
                {/* Header Row */}
                <div className="flex items-center justify-between gap-3">
                  <span className="font-semibold text-base">
                    {alert.item.title}
                  </span>
                  {/* Status Badge - Right aligned */}
                  {alert.isLive ? (
                    <span className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-primary px-3 py-1.5 font-semibold text-primary-foreground text-sm shadow-sm">
                      <div className="size-2 animate-pulse rounded-full bg-primary-foreground" />
                      ກຳລັງຮຽນ
                    </span>
                  ) : (
                    alert.countdownText && (
                      <span className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-amber-500 px-3 py-1.5 font-semibold text-sm text-white shadow-sm">
                        <ClockIcon className="size-4" />
                        {alert.countdownText}
                      </span>
                    )
                  )}
                </div>

                {/* Details Row */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <TypeIcon className="size-4" />
                    <span>{getTypeLabel(alert.item.type)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 font-medium text-foreground">
                    <MapPinIcon className="size-4 text-muted-foreground" />
                    <span>{alert.item.roomName}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <ClockIcon className="size-4" />
                    <span>
                      {startFormatted} – {endFormatted}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
